/**
 * A notice that takes itself away.
 *
 * WHY THIS IS ONE COMPONENT
 *
 * Five screens rendered `<p role="status" className="notice">` with their own
 * `notice` state. Adding a countdown, a dismiss control and a fade to each of
 * them would have been five copies of a timer, which is the shape `money.ts`
 * and `labels.ts` already exist to avoid.
 *
 * A FADE, NOT A PROGRESS BAR (the operator's decision, 2026-09-13). The bar
 * filled across the box, and a bar filling towards its end reads as something
 * about to happen. A notice that simply fades away says it is going, which is
 * all it is doing.
 *
 * ONE CLOCK, AND THAT IS THE WHOLE DESIGN
 *
 * The obvious build is a CSS transition for the fade plus a `setTimeout` for the
 * dismissal. That is two clocks for one event and they drift the moment
 * somebody hovers: the transition pauses, the timeout does not, and the toast
 * vanishes while still half visible. Nothing reports the disagreement — it
 * just looks broken occasionally.
 *
 * So elapsed time is a single piece of state. The opacity is computed from it
 * and the dismissal happens when it runs out. Pausing is one flag affecting one
 * clock, so the fade and the disappearance cannot disagree. While held, the
 * toast is shown at full opacity: a half-faded sentence is the one being read.
 *
 * It also survives `prefers-reduced-motion`. A toast keyed to `animationend`
 * never leaves when the viewer has animations turned off, because the event
 * never fires. This has no animation to disable.
 *
 * HOVER IS AN ENHANCEMENT, NEVER THE ONLY WAY OUT
 *
 * `Combobox`'s sibling rule applies: hover has no touch equivalent and no
 * keyboard one. Pausing on hover is a convenience for a mouse; the dismiss
 * button is what makes the toast controllable for everyone. FOCUS PAUSES TOO,
 * so somebody tabbing to that button does not watch it disappear under them.
 *
 * WHAT DOES NOT BELONG IN HERE
 *
 * Anything that is a standing FACT rather than an event. "This entry is
 * posted" describes the entry and must not time out; the editor renders that
 * as a plain notice still. Offers are the other exclusion — the alias and rule
 * capture points must not expire, because a prompt nobody sees is how the
 * rules table stays empty.
 */

import { useEffect, useRef, useState } from 'react';

/** Five seconds for every toast, rather than varying by message. */
const DURATION_MS = 5_000;

/**
 * How often elapsed time advances.
 *
 * 50ms is smooth enough for a bar crossing a box in five seconds and cheap
 * enough that a re-render per tick does not matter for one line of text.
 */
const TICK_MS = 50;

export interface ToastProps {
  message: string;
  onDismiss: () => void;
  /** `ok` is the green confirmation; omit for the neutral grey one. */
  variant?: 'ok';
  /**
   * One action offered inside the toast, for undoing what it announces.
   *
   * IT IS NOT A SECOND DISMISS. The toast still times out on its own, and the
   * action is gone with it -- which is the point: an undo the operator has to
   * hunt for later is a settings screen, and an undo that never expires is a
   * decision left open forever. Five seconds and the pause-on-hover are what
   * make a disappearing action fair.
   */
  action?: { label: string; onAction: () => void };
  /** Overridable for tests. Production uses the default. */
  durationMs?: number;
}

export function Toast({
  message,
  onDismiss,
  variant,
  action,
  durationMs = DURATION_MS,
}: ToastProps) {
  const [elapsed, setElapsed] = useState(0);
  const [held, setHeld] = useState(false);

  /*
   * `onDismiss` through a ref so the countdown effect does not depend on it.
   * Every caller passes an inline arrow, so a new function identity arrives on
   * each render of the parent -- put it in the dependency array and the
   * interval is torn down and rebuilt constantly, which resets the clock and
   * the toast never reaches the end.
   */
  const dismiss = useRef(onDismiss);
  useEffect(() => {
    dismiss.current = onDismiss;
  }, [onDismiss]);

  /*
   * A new message restarts the countdown.
   *
   * KNOWN EDGE, stated rather than hidden: setting the SAME message twice
   * inside one countdown does not restart it, because React sees no state
   * change and this effect does not run. Saving twice in five seconds shows
   * one toast that leaves five seconds after the first save. Live with it
   * until it matters; the fix is a nonce from the caller.
   */
  useEffect(() => {
    setElapsed(0);
    setHeld(false);
  }, [message]);

  useEffect(() => {
    if (held) return;
    const id = setInterval(() => setElapsed((value) => value + TICK_MS), TICK_MS);
    return () => clearInterval(id);
  }, [held]);

  useEffect(() => {
    if (elapsed >= durationMs) dismiss.current();
  }, [elapsed, durationMs]);

  // Full while held; otherwise from 1 down to 0 over the duration, never below.
  const opacity = held ? 1 : Math.max(0, 1 - elapsed / durationMs);

  return (
    <div
      role="status"
      className={`notice toast${variant === 'ok' ? ' notice-ok' : ''}`}
      style={{ opacity }}
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      // Focus and blur bubble in React, so this covers the dismiss button
      // inside without a listener of its own.
      onFocus={() => setHeld(true)}
      onBlur={() => setHeld(false)}
    >
      <span className="toast-text">{message}</span>
      {action && (
        <button
          type="button"
          className="link toast-action"
          onClick={() => {
            action.onAction();
            dismiss.current();
          }}
        >
          {action.label}
        </button>
      )}
      <button
        type="button"
        className="toast-dismiss"
        aria-label="Dismiss"
        onClick={() => dismiss.current()}
      >
        ×
      </button>
    </div>
  );
}
